import { EXPORT_WARNING_HEADER } from '@cpce/shared';
import axios from 'axios';
import { smartJSONParse } from './parse.utils';

export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // A failed download's JSON error arrives as a Blob — typed as the file it would have been,
    // since `@Header()` applies before the handler runs. Unparsed, every toast and the Sentry
    // filter only see « Request failed with status code … ».
    const data = error.response?.data;
    if (data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await data.text());
      } catch {
        // Not JSON after all: keep the raw body.
      }
    }
    const isAuthPage =
      window.location.pathname.startsWith('/login') ||
      window.location.pathname.startsWith('/reset-password');
    if (error.response?.status === 401 && !isAuthPage) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export function apiGet<T>(url: string, params?: object): Promise<T> {
  return api
    .get<T>(url, {
      params,
      transformResponse: [smartJSONParse],
    })
    .then((res) => res.data);
}

export function apiGetBlob(url: string, params?: object): Promise<Blob> {
  return apiGetBlobWithWarning(url, params).then(({ blob }) => blob);
}

export function apiGetBlobWithWarning(
  url: string,
  params?: object
): Promise<{ blob: Blob; warning: string | null }> {
  return api.get<Blob>(url, { params, responseType: 'blob' }).then((res) => {
    const warning = res.headers[EXPORT_WARNING_HEADER.toLowerCase()];
    return {
      blob: res.data,
      warning: typeof warning === 'string' ? decodeURIComponent(warning) : null,
    };
  });
}

// Mutations need the same parser as reads: the API serialises Decimal as `{ _decimal }`, and an
// unrehydrated one breaks on its first `toFixed`.
export function apiPost<T>(url: string, body?: object): Promise<T> {
  return api.post<T>(url, body, { transformResponse: [smartJSONParse] }).then((res) => res.data);
}

export function apiPatch<T>(url: string, body?: object): Promise<T> {
  return api.patch<T>(url, body, { transformResponse: [smartJSONParse] }).then((res) => res.data);
}

export function apiDelete<T>(url: string): Promise<T> {
  return api.delete<T>(url, { transformResponse: [smartJSONParse] }).then((res) => res.data);
}

export function apiPut<T>(url: string, body?: object): Promise<T> {
  return api.put<T>(url, body, { transformResponse: [smartJSONParse] }).then((res) => res.data);
}

export function apiUploadFile<T>(url: string, file: File): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);
  return api
    .post<T>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data);
}
